import { gsap } from "gsap";
import { IDestroyOptions, Point, Text, TextStyle, Texture } from "pixi.js";
import { ICanvasScaler } from "../../../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../../../shared/async/PromiseCompletionSource";
import { GameFont } from "../../../../../common/GameFont";
import { BasePageView } from "../../../../../common/components/BasePageView";
import { ButtonWithScaleAnimation } from "../../../../../common/components/ButtonWithScaleAnimation";
import { CommonResourcesPackage } from "../../../../../common/resources/CommonResourcesPackage";
import { SoundsResourcePackageWrapper } from "../../../../../common/resources/SoundsResourcePackageWrapper";
import { FadeScreen } from "../../../../../game/components/FadeScreen";
import { DicesBackground } from "../../components/DicesBackgound";
import { WheelRewardContainerNew } from "../../components/WheelRewardContainerNew";
import { HourlyBonusResourcePackage } from "../../resources/HourlyBonusResourcePackage";
import { RollProgressCounter } from "../../../../../common/components/RollProgressCounter";
import { DiceCollectAnimationView } from "../../../../gift/components/giftsummary/DiceCollectAnimationView";
import { IPlayerModel } from "../../../../../common/models/IPlayerModel";

export class WheelDiceRollsPage extends BasePageView {
    private static readonly COLLECT_BUTTON_TEXT = "COLLECT";

    private readonly playerModel: IPlayerModel;
    private readonly commonResources: CommonResourcesPackage;
    private readonly wheelResources: HourlyBonusResourcePackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly viewCompletionSource: PromiseCompletionSource<void>;
    private fadeScreen: FadeScreen;
    private dicesBackground: DicesBackground;
    private rewardContainer: WheelRewardContainerNew;
    private collectButton: ButtonWithScaleAnimation;
    private collectButtonText: Text;
    private rollsCount: number;
    private rollProgress: RollProgressCounter;
    private collectAnimation: DiceCollectAnimationView;
    private collectAnimationPoint: Point;

    public constructor(commonResources: CommonResourcesPackage,
        wheelResources: HourlyBonusResourcePackage,
        soundResources: SoundsResourcePackageWrapper,
        canvasScaler: ICanvasScaler,
        playerModel: IPlayerModel,
        viewCompletionSource: PromiseCompletionSource<void>,
        rollsCount: number
    ) {
        super(canvasScaler);
        this.commonResources = commonResources;
        this.wheelResources = wheelResources;
        this.soundResources = soundResources;
        this.playerModel = playerModel;
        this.viewCompletionSource = viewCompletionSource;
        this.rollsCount = rollsCount;
        this.build();
        this.soundResources.assets_sounds_goldenwheel_new_wheel_win_screen_mp3.play();
        this.once("added", this.show.bind(this));
    }

    protected override build(): void {
        this.fadeScreen = new FadeScreen(0.5, 0, 0.8, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.addChild(this.fadeScreen);
        this.dicesBackground = new DicesBackground(this.wheelResources, this.canvasScaler);
        this.addChild(this.dicesBackground);
        this.rewardContainer = new WheelRewardContainerNew(this.wheelResources.assets_views_hourly_bonus_wheel_result_dice_wheel_result_png, this.rollsCount);
        this.addChild(this.rewardContainer);
        this.collectButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_button_green_png);
        this.collectButton.pivot.set(this.collectButton.width * 0.5, this.collectButton.height * 0.5);
        this.collectButtonText = new Text(WheelDiceRollsPage.COLLECT_BUTTON_TEXT, new ButtonTextStyle());
        this.collectButton.addChild(this.collectButtonText);
        this.collectButton.onUp.add(this.onCollectButton, this);
        this.addChild(this.collectButton);
        this.rollProgress = new RollProgressCounter(
            this.commonResources.assets_common_ui_progress_counter_bottom_progress_png,
            this.commonResources.assets_common_ui_progress_counter_yellow_progress_png,
            this.playerModel.rolls.value - this.rollsCount
        );
        this.addChild(this.rollProgress);
        this.collectAnimationPoint = new Point();
        this.collectAnimation = new DiceCollectAnimationView(
            [this.commonResources.assets_common_ui_dices_dice_01_png, this.commonResources.assets_common_ui_dices_dice_03_png],
            Texture.EMPTY,
            this.rollsCount,
            this.collectAnimationPoint
        );
        this.collectAnimation.diceCollected.add(this.rollProgress.incrementProgress, this.rollProgress);
        this.addChild(this.collectAnimation);
        super.build();
    }

    private async show(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        this.fadeScreen.fadeIn();
        this.rewardContainer.playStartAnimation();
        this.dicesBackground.playStartAnimation();
        animationTimeLine.fromTo(this.collectButton.scale, { x: 0, y: 0 }, { x: 1, y: 1, duration: 0.5, ease: "back.out(2)" }, "<");
        await animationTimeLine;
        animationTimeLine.kill();
    }

    private async onCollectButton(): Promise<void> {
        this.collectButton.enabled = false;
        this.soundResources.assets_sounds_goldenwheel_new_wheel_collect_button_click_mp3.play();
        const animationTimeLine = new gsap.core.Timeline();
        await animationTimeLine.addPause(0.3);
        animationTimeLine.play();
        this.soundResources.assets_sounds_goldenwheel_new_wheel_win_screen_disappear_mp3.play();
        await this.collectAnimation.startAnimation();
        this.dicesBackground.hideDices();
        this.rewardContainer.hideContainer();
        animationTimeLine.fromTo(this.collectButton.scale, { x: 1, y: 1 }, { x: 0, y: 0, duration: 0.8 }, "<");
        await animationTimeLine;
        animationTimeLine.kill();
        this.viewCompletionSource.complete();
    }

    protected override layout(width: number, height: number): void {
        this.fadeScreen.width = width;
        this.fadeScreen.height = height;
        this.rewardContainer.position.set(width * 0.5, 770);
        this.collectButton.position.set(width * 0.5, 1273 + this.collectButton.height);
        this.collectButtonText.position.set(this.collectButton.width * 0.5 - this.collectButtonText.width * 0.5, this.collectButton.height * 0.5 - this.collectButtonText.height * 0.5);
        this.collectAnimation.position.set(width * 0.5, height * 0.5);
        this.rollProgress.position.set(width * 0.5, 1695);
        this.collectAnimationPoint.set(this.rollProgress.position.x - this.collectAnimation.position.x, this.rollProgress.position.y - this.collectAnimation.position.y);
        super.layout(width, height);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.collectButton.onUp.remove(this.onCollectButton, this);
        this.collectAnimation.diceCollected.remove(this.rollProgress.incrementProgress, this.rollProgress);
        super.destroy(options);
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            dropShadow: true,
            dropShadowAngle: 0.7,
            dropShadowBlur: 5,
            dropShadowDistance: 2,
            dropShadowAlpha: 0.5,
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 80,
            miterLimit: 0
        }));
    }
}
